import {
  MessageBar,
  MessageBarBody,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Phone16Regular } from '@fluentui/react-icons';
import { useT } from '@powerportalspro/react';
import type { TableRecord } from '@powerportalspro/core';
import {
  GridColumn,
  GridColumns,
  GridTemplateColumn,
  Highlighter,
  HighlightFull,
  matchesNumericSearch,
  parseNumericSearch,
  MainGrid,
  TextEdit,
  resolveColumnDisplayValue,
  type ViewDefinition,
} from '@powerportalspro/react-fluent';

// Mirrors Blazor `GridColumnsMainDemo`: a `<MainGrid>` over the contact
// table with a mix of column flavours that exercise the
// `<GridColumns>` / `<GridColumn>` / `<GridTemplateColumn>` surface:
//
//   - "Full Name" — `<GridTemplateColumn>` with a custom render that
//     composes firstname + lastname into an initials-avatar + bold name
//     cell. Read-only renderer + an editor renderer (drop-in TextEdit
//     pair) for when the grid flips into edit mode.
//   - "Email" — `<GridColumn>` with a custom cell renderer that wraps
//     the formatted value in a `mailto:` link.
//   - "Phone" — `<GridColumn>` whose renderer prefixes the formatted
//     value with an inline icon.
//   - "Age" — `<GridColumn>` with conditional formatting on a numeric
//     value (red / amber / green badges).
//   - "Birthday" — a plain `<GridColumn>` with no renderer, so the
//     built-in date cell renders it (and highlights it for `>1990`).
//
// The grid uses the same "All Contacts" custom view as the
// Account/Contact demos so the data shape is consistent across the site.

const STRINGS_BASE =
  'components.PowerPortalsPro.Demo.Client.Customizations.Pages.Grids.GridColumns.GridColumnsMainDemo';

// Inline view (same idea as Blazor's `AccountAndContactCustomViews.AllContactsViewId`):
// pins the grid to a stable view definition so the demo doesn't depend
// on a specific environment's saved-view GUIDs.
const ALL_CONTACTS_VIEW: ViewDefinition = {
  id: '00000000-0000-0000-0000-000000000001',
  tableName: 'contact',
  displayName: 'All Contacts',
  columns: [
    { columnName: 'fullname', width: 200 },
    { columnName: 'emailaddress1', width: 250 },
    { columnName: 'mobilephone', width: 160 },
    { columnName: 'ppp_age', width: 90 },
    { columnName: 'birthdate', width: 125 },
  ],
  fetchXml: `<fetch>
    <entity name="contact">
      <attribute name="firstname" />
      <attribute name="lastname" />
      <attribute name="emailaddress1" />
      <attribute name="mobilephone" />
      <attribute name="ppp_age" />
      <attribute name="birthdate" />
      <order attribute="lastname" />
    </entity>
  </fetch>`,
};

const useStyles = makeStyles({
  // Initials avatar circle in the "Full Name" cell. Picks up the brand
  // accent so consumers using the accent picker see it shift with the
  // rest of the chrome.
  avatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    fontSize: '11px',
    fontWeight: 600,
  },
  nameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  editRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
  phoneRow: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
  },
});

// Pull a string value out of a TableRecord's column. Uses the canonical
// `resolveColumnDisplayValue` helper (same one the built-in TextCell uses)
// which checks `formattedValues` first, then walks the `properties` typed
// union with `$type` discrimination, and unwraps `RelatedRecordValue`
// link-entity columns. Returns empty string when absent.
function readString(record: TableRecord | null | undefined, column: string): string {
  if (!record) return '';
  return resolveColumnDisplayValue(record, column) ?? '';
}

// Read the raw numeric value (NOT the display string) for threshold
// comparisons. Goes straight to `properties[column].value` and casts to
// number; falls through to null when the property is absent or non-numeric.
function readNumber(record: TableRecord | null | undefined, column: string): number | null {
  const cell = record?.properties?.[column];
  if (cell && typeof cell === 'object' && 'value' in cell) {
    const v = (cell as { value?: unknown }).value;
    if (typeof v === 'number') return v;
    if (typeof v === 'string' && v.length > 0) {
      const parsed = Number(v);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

export function GridColumnsMainSample() {
  const styles = useStyles();
  const t = useT();
  return (
    <>
      <MessageBar intent="warning" layout="multiline" style={{ gridTemplateRows: 'auto', minHeight: 'auto' }}>
        <MessageBarBody>{t(`${STRINGS_BASE}.login-required`)}</MessageBarBody>
      </MessageBar>

      <MainGrid
        tableName="contact"
        minHeight="400px"
        maxHeight="400px"
        pageSize={10}
        allowEdit
        customViewDefinitions={[ALL_CONTACTS_VIEW]}
        defaultViewId={ALL_CONTACTS_VIEW.id}
        viewIds={[ALL_CONTACTS_VIEW.id]}
      >
        <GridColumns>
          {/* Template column — composes firstname + lastname into a
              single bold "Full Name" cell with an initials avatar. The
              fullname Dataverse column is NOT fetched; the cell builds
              the display string client-side from the two raw parts. */}
          <GridTemplateColumn
            displayName="Full Name"
            width="220px"
            dependsOn={['firstname', 'lastname']}
            sortBy="lastname"
            // Both names are in the grid's search; `searchText` (set only
            // because of `searchBy`) lets <Highlighter> mark the match.
            searchBy={['firstname', 'lastname']}
            cellRenderer={({ record, searchText }) => {
              const first = readString(record, 'firstname');
              const last = readString(record, 'lastname');
              const initials = (first.charAt(0) + last.charAt(0)).toUpperCase();
              const display = `${first} ${last}`.trim();
              return (
                <div className={styles.nameRow}>
                  <span className={styles.avatar}>{initials || '—'}</span>
                  <strong>
                    <Highlighter text={display || '—'} searchText={searchText ?? null} />
                  </strong>
                </div>
              );
            }}
            editRenderer={() => (
              // Drop-in editor: two TextEdit components against
              // firstname / lastname. Identical to placing them inside
              // a bound `<GridColumn>`'s edit-mode renderer — the row's
              // record context flows through automatically.
              <div className={styles.editRow}>
                <TextEdit columnName="firstname" displayLabelWhenAvailable={false} />
                <TextEdit columnName="lastname" displayLabelWhenAvailable={false} />
              </div>
            )}
          />

          {/* Email — render as a mailto: link when populated. `emailaddress1`
              is a plain string column, so the raw value IS the display
              value. `formattedValues` is only server-populated for columns
              that need a display formatting different from the raw value
              (dates, money, choices, lookups, bools), so reach into
              `properties[].value` directly.

              The mailto URL puts the address in the standard addr-spec
              slot AND adds a `to=` hfield with the `"First Last" <email>`
              mailbox form. RFC 6068 §5 explicitly supports display names
              inside the `to` hfield (most clients honor it; clients that
              don't fall back gracefully to the bare addr-spec). Encoding
              the entire `"Name" <email>` string as the addr-spec — the
              earlier approach — gets dropped by most clients because
              addr-spec doesn't allow display names per the URI grammar.
              The cell text stays as the email itself so the column reads
              as an email reference. */}
          <GridColumn
            columnName="emailaddress1"
            displayName="Email"
            width="260px"
            cellRenderer={({ record, searchText }) => {
              const email = readString(record, 'emailaddress1');
              if (!email) return <span>-</span>;
              const first = readString(record, 'firstname');
              const last = readString(record, 'lastname');
              const display = `${first} ${last}`.trim();
              const href = display
                ? `mailto:${email}?to=${encodeURIComponent(`"${display}" <${email}>`)}`
                : `mailto:${email}`;
              // `searchText` marks what the grid's search matched, as the
              // built-in cells do.
              return (
                <a href={href}>
                  <Highlighter text={email} searchText={searchText ?? null} />
                </a>
              );
            }}
          />

          {/* Phone — raw string prefixed with an inline icon. Same
              raw-string treatment as email. */}
          <GridColumn
            columnName="mobilephone"
            displayName="Phone"
            width="180px"
            cellRenderer={({ record, searchText }) => {
              const phone = readString(record, 'mobilephone');
              if (!phone) return <span>-</span>;
              return (
                <div className={styles.phoneRow}>
                  <Phone16Regular />
                  <Highlighter text={phone} searchText={searchText ?? null} />
                </div>
              );
            }}
          />

          {/* Conditional formatting: raw numeric value drives the badge
              color, formatted value drives the display. Demonstrates the
              raw-vs-formatted split. */}
          <GridColumn
            columnName="ppp_age"
            displayName="Age"
            width="100px"
            align="center"
            cellRenderer={({ record, searchText }) => {
              const ageVal = readNumber(record, 'ppp_age');
              // Use the canonical display-value resolver for the badge
              // label — falls back from `formattedValues` to raw value
              // via the same path TextCell uses.
              const displayText = readString(record, 'ppp_age') || String(ageVal ?? '');
              if (ageVal === null) return <span>-</span>;
              const { bg, fg } =
                ageVal > 65
                  ? { bg: tokens.colorStatusDangerBackground3, fg: tokens.colorNeutralForegroundOnBrand }
                  : ageVal > 40
                    ? { bg: tokens.colorStatusWarningBackground3, fg: tokens.colorNeutralForeground1 }
                    : { bg: tokens.colorStatusSuccessBackground3, fg: tokens.colorNeutralForegroundOnBrand };
              // A number column's search is a comparison (`>40`): mark the
              // whole value when it holds, as the built-in number cells do.
              const parsed = parseNumericSearch(searchText);
              const label =
                parsed && matchesNumericSearch(ageVal, parsed) ? <HighlightFull text={displayText} /> : displayText;
              return (
                <span
                  style={{
                    backgroundColor: bg,
                    color: fg,
                    padding: '2px 8px',
                    borderRadius: '10px',
                    fontSize: '12px',
                  }}
                >
                  {label}
                </span>
              );
            }}
          />
          {/* Birthday — no cellRenderer, so the built-in date cell renders
              it, and highlights it for a date search such as `>1990`. */}
          <GridColumn columnName="birthdate" width="125px" />
        </GridColumns>
      </MainGrid>
    </>
  );
}
